import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

/** One row per trace_id (same trace shows once; primary = first in list order). */
function groupEligibleByTrace(rows) {
  const byTrace = new Map();
  rows.forEach((r) => {
    const key = r.trace_id != null && r.trace_id !== "" ? r.trace_id : r.booking_id;
    if (!byTrace.has(key)) byTrace.set(key, r);
  });
  return Array.from(byTrace.values());
}

const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};
const money2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "0.00");

export default function GiftCardRefunds() {
  const [tab, setTab] = useState("eligible");

  const [eligibleRows, setEligibleRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);

  const [metaEligible, setMetaEligible] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  const [metaHistory, setMetaHistory] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [pageEligible, setPageEligible] = useState(1);
  const [pageHistory, setPageHistory] = useState(1);

  const [refundOpen, setRefundOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const displayEligibleRows = useMemo(() => groupEligibleByTrace(eligibleRows), [eligibleRows]);

  const [form, setForm] = useState({
    cancellation_amount: "",
    cancel_type: "Change Itinerary / Reissue",
    refund_type: "NORMAL_REFUND",
    instant_refund_reason: "",
    full_payment: false,
    cancel_insurance: false,
    remarks: "",
    attachment: null,
  });

  const fetchEligible = async (opts = {}) => {
    const nextPage = opts.page ?? pageEligible;
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/gift-card-refunds/eligible", {
        params: {
          search: search || undefined,
          page: nextPage,
          limit: 20,
        },
      });

      setEligibleRows(res.data?.data || []);
      setMetaEligible(
        res.data?.meta || { total: 0, page: nextPage, limit: 20, totalPages: 1 }
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message || e.message || "Failed to load eligible bookings";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (opts = {}) => {
    const nextPage = opts.page ?? pageHistory;
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/gift-card-refunds/history", {
        params: {
          search: search || undefined,
          page: nextPage,
          limit: 20,
        },
      });

      setHistoryRows(res.data?.data || []);
      setMetaHistory(
        res.data?.meta || { total: 0, page: nextPage, limit: 20, totalPages: 1 }
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message || e.message || "Failed to load refund history";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "eligible") fetchEligible({ page: 1 });
    else fetchHistory({ page: 1 });
  }, []);

  useEffect(() => {
    if (tab === "eligible") fetchEligible({ page: pageEligible });
    else fetchHistory({ page: pageHistory });
  }, [tab, pageEligible, pageHistory]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (tab === "eligible") {
        setPageEligible(1);
        fetchEligible({ page: 1 });
      } else {
        setPageHistory(1);
        fetchHistory({ page: 1 });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [search]);

  const openRefundModal = (row) => {
    setSelected(row);
    setForm({
      cancellation_amount: row?.cancellation_amount != null ? String(row.cancellation_amount) : "",
      cancel_type: "Change Itinerary / Reissue",
      refund_type: "NORMAL_REFUND",
      instant_refund_reason: "",
      full_payment: false,
      cancel_insurance: false,
      remarks: "",
      attachment: null,
    });
    setRefundOpen(true);
  };

  const closeRefundModal = () => {
    setRefundOpen(false);
    setSelected(null);
    setSubmitting(false);
    setForm({
      cancellation_amount: "",
      cancel_type: "Change Itinerary / Reissue",
      refund_type: "NORMAL_REFUND",
      instant_refund_reason: "",
      full_payment: false,
      cancel_insurance: false,
      remarks: "",
      attachment: null,
    });
  };

  const totalFareNum = useMemo(() => toNum(selected?.totalfare), [selected?.totalfare]);
  const convenienceFeeNum = useMemo(() => toNum(selected?.convenience_fee), [selected?.convenience_fee]);
  const appliedGiftcardAmountNum = useMemo(() => toNum(selected?.applied_giftcard_amount), [selected?.applied_giftcard_amount]);
  const isGiftcardFullyClaimed = useMemo(() => Number(selected?.is_giftcard_fully_claimed || 0) === 1, [selected?.is_giftcard_fully_claimed]);

  const cancellationAmountNum = useMemo(() => toNum(form.cancellation_amount), [form.cancellation_amount]);

  const maxCancellationAllowed = useMemo(() => {
    if (form.full_payment) return 0;
    return Math.max(0, totalFareNum);
  }, [form.full_payment, totalFareNum]);


  const maxRefundableForPartialGiftcard = useMemo(() => {
    if (!selected) return 0;
    if (isGiftcardFullyClaimed) return Infinity;
    return Math.max(0, totalFareNum - convenienceFeeNum - appliedGiftcardAmountNum);
  }, [selected, isGiftcardFullyClaimed, totalFareNum, convenienceFeeNum, appliedGiftcardAmountNum]);

  const refundCalculation = useMemo(() => {
    if (!selected) {
      return {
        overall_refundable_amount: 0,
        giftcard_refund_amount: 0,
        to_user_refund_amount: 0,
        amount_to_giftcard_wallet: 0,
        case_description: "NO_SELECTION",
        errors: [],
      };
    }

    const fullPayment = form.full_payment;

    let cancelAmt = cancellationAmountNum;

    const errors = [];

    if (!fullPayment && cancelAmt > totalFareNum) {
      errors.push("Cancellation amount cannot be greater than Total Fare");
      cancelAmt = totalFareNum; 
    }
    if (cancelAmt < 0) {
      errors.push("Cancellation amount cannot be negative");
      cancelAmt = 0;
    }

    if (fullPayment && isGiftcardFullyClaimed) {
      return {
        overall_refundable_amount: totalFareNum,
        giftcard_refund_amount: totalFareNum,
        to_user_refund_amount: 0,
        amount_to_giftcard_wallet: totalFareNum,
        case_description: "CASE_1: Full payment + Fully claimed = Full refund to giftcard wallet",
        errors,
        cancelAmtUsed: 0,
      };
    }

    if (!fullPayment && isGiftcardFullyClaimed) {
      const refundable = Math.max(0, totalFareNum - convenienceFeeNum - cancelAmt);
      return {
        overall_refundable_amount: refundable,
        giftcard_refund_amount: refundable,
        to_user_refund_amount: 0,
        amount_to_giftcard_wallet: refundable,
        case_description: "CASE_2: Fully claimed = (Total - Conv Fee - Cancellation) to giftcard wallet",
        errors,
        cancelAmtUsed: cancelAmt,
      };
    }

    if (!isGiftcardFullyClaimed) {
      const refundableRaw = Math.max(0, totalFareNum - convenienceFeeNum - cancelAmt);
      const capBase = Math.max(0, totalFareNum - convenienceFeeNum - appliedGiftcardAmountNum);

      let refundable = refundableRaw;

      // --- VALIDATION 2: refundable should not exceed capBase ---
      // if (refundableRaw > capBase) {
      //   errors.push(
      //     "Refundable amount exceeded max allowed for partial giftcard (Total - Convenience Fee - Giftcard Amount)."
      //   );
      //   refundable = capBase; // clamp for preview, but submit will block
      // }

      const giftcardRefund = Math.min(refundable, appliedGiftcardAmountNum);
      const toUserRefund = Math.max(0, refundable - giftcardRefund);

      return {
        overall_refundable_amount: refundable,
        giftcard_refund_amount: giftcardRefund,
        to_user_refund_amount: toUserRefund,
        amount_to_giftcard_wallet: giftcardRefund,
        case_description: fullPayment
          ? "CASE_3A: Full payment + Partial claimed = Split refund"
          : "CASE_3B: Partial + Partial claimed = Split refund",
        errors,
        cancelAmtUsed: cancelAmt,
        capBase,
        refundableRaw,
      };
    }

    return {
      overall_refundable_amount: 0,
      giftcard_refund_amount: 0,
      to_user_refund_amount: 0,
      amount_to_giftcard_wallet: 0,
      case_description: "UNKNOWN_CASE",
      errors,
      cancelAmtUsed: cancellationAmountNum,
    };
  }, [
    selected,
    form.full_payment,
    isGiftcardFullyClaimed,
    totalFareNum,
    convenienceFeeNum,
    cancellationAmountNum,
    appliedGiftcardAmountNum,
  ]);

  useEffect(() => {
    if (!refundOpen) return;
    if (!selected) return;
    if (form.full_payment) return; 

    const raw = form.cancellation_amount;
    if (raw === "" || raw === null || raw === undefined) return;

    const n = toNum(raw);

    if (n < 0) {
      setForm((p) => ({ ...p, cancellation_amount: "0" }));
      toast.error("Cancellation amount cannot be negative");
      return;
    }

    if (n > maxCancellationAllowed) {
      setForm((p) => ({ ...p, cancellation_amount: String(maxCancellationAllowed) }));
      toast.error("Cancellation amount cannot be greater than Total Fare");
    }
  }, [form.cancellation_amount, refundOpen, selected, form.full_payment, maxCancellationAllowed]);

  const submitGiftcardRefund = async (e) => {
    e.preventDefault();
    if (!selected) return;

    if (!selected?.booking_id) {
      toast.error("booking_id is missing from selected row");
      return;
    }

    // Validation: server-relevant checks (block submit)
    if (!form.full_payment && cancellationAmountNum > totalFareNum) {
      toast.error("Cancellation amount cannot be greater than Total Fare");
      return;
    }

    if (!isGiftcardFullyClaimed) {
      const refundableRaw = Math.max(0, totalFareNum - convenienceFeeNum - cancellationAmountNum);
      const capBase = Math.max(0, totalFareNum - convenienceFeeNum - appliedGiftcardAmountNum);
      if (refundableRaw > capBase) {
        toast.error(
          "Invalid amounts: Refundable exceeds allowed limit (Total - Convenience Fee - Giftcard Amount)."
        );
        return;
      }
    }

    if (form.refund_type === "INSTANT_REFUND" && !form.instant_refund_reason?.trim()) {
      toast.error("Instant refund reason is required");
      return;
    }

    if (!form.remarks?.trim()) {
      toast.error("Remarks is required");
      return;
    }

    if (form.cancel_type === "-Select-") {
      toast.error("Please select a cancellation type");
      return;
    }

    if (refundCalculation?.errors?.length) {
      toast.error(refundCalculation.errors[0]);
      return;
    }

    try {
      setSubmitting(true);

      const fd = new FormData();

      fd.append("booking_id", String(selected.booking_id));
      fd.append("booking_ticket_number", String(selected.booking_ticket_number));
      fd.append("payment_id", String(selected.payment_id || ""));
      fd.append("booking_payment_id", String(selected.booking_payment_id || ""));
      fd.append("user_id", String(selected.user_id || ""));

      fd.append("totalfare", String(selected.totalfare || "0"));
      fd.append("convenience_fee", String(selected.convenience_fee || "0"));
      fd.append("applied_giftcard_amount", String(selected.applied_giftcard_amount || "0"));
      fd.append("is_giftcard_fully_claimed", String(selected.is_giftcard_fully_claimed || 0));

      fd.append("cancellation_amount", String(form.cancellation_amount || "0"));
      fd.append("cancel_type", String(form.cancel_type));
      fd.append("refund_type", String(form.refund_type));
      fd.append("instant_refund_reason", String(form.instant_refund_reason || ""));
      fd.append("full_payment", form.full_payment ? "1" : "0");
      fd.append("cancel_insurance", form.cancel_insurance ? "1" : "0");
      fd.append("remarks", String(form.remarks).trim());

      fd.append("overall_refundable_amount", String(refundCalculation.overall_refundable_amount));
      fd.append("giftcard_refund_amount", String(refundCalculation.giftcard_refund_amount));
      fd.append(
        "to_user_refund_amount_paise",
        String(Math.round(refundCalculation.to_user_refund_amount * 100))
      );
      fd.append("amount_to_giftcard_wallet", String(refundCalculation.amount_to_giftcard_wallet));

      if (form.attachment) fd.append("attachment", form.attachment);

      await api.post("/admin/gift-card-refunds/process", fd);

      toast.success("Gift card refund processed successfully");
      closeRefundModal();

      if (tab === "eligible") await fetchEligible({ page: metaEligible.page });
      else await fetchHistory({ page: metaHistory.page });

      await fetchEligible({ page: 1 });
      setPageEligible(1);
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Refund failed");
    } finally {
      setSubmitting(false);
    }
  };

  const goPrev = () => {
    if (tab === "eligible") {
      if (metaEligible.page <= 1 || loading) return;
      const p = metaEligible.page - 1;
      setPageEligible(p);
      fetchEligible({ page: p });
    } else {
      if (metaHistory.page <= 1 || loading) return;
      const p = metaHistory.page - 1;
      setPageHistory(p);
      fetchHistory({ page: p });
    }
  };

  const goNext = () => {
    if (tab === "eligible") {
      if (metaEligible.page >= metaEligible.totalPages || loading) return;
      const p = metaEligible.page + 1;
      setPageEligible(p);
      fetchEligible({ page: p });
    } else {
      if (metaHistory.page >= metaHistory.totalPages || loading) return;
      const p = metaHistory.page + 1;
      setPageHistory(p);
      fetchHistory({ page: p });
    }
  };

  const activeMeta = tab === "eligible" ? metaEligible : metaHistory;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Gift Card Refunds</h1>
          {/* <p className="text-sm text-gray-600 mt-1">
            Eligible bookings are fetched from <b>bookings</b> where{" "}
            <b>is_giftcard_applied = 1</b>. Refund logic handles full/partial giftcard claims.
          </p> */}
        </div>
      </div>

      <div className="mt-4 flex gap-2 border-b">
        <button
          type="button"
          onClick={() => setTab("eligible")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
            tab === "eligible" ? "border-black text-black" : "border-transparent text-gray-600"
          }`}
        >
          Eligible
        </button>

        <button
          type="button"
          onClick={() => setTab("history")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
            tab === "history" ? "border-black text-black" : "border-transparent text-gray-600"
          }`}
        >
          History
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[280px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Booking ID / User ID / Payment ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>{tab === "eligible" ? "Eligible Giftcard Bookings" : "Giftcard Refund History"}</span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={activeMeta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{activeMeta.page}</b> / <b>{activeMeta.totalPages}</b> (Total:{" "}
              <b>{activeMeta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={activeMeta.page >= metaEligible.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : tab === "eligible" ? (
          displayEligibleRows.length === 0 ? (
            <div className="p-4 text-sm text-gray-600">No eligible bookings found.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  {/* <th className="px-4 py-3">Booking ID</th> */}
                  <th className="px-4 py-3">Booking Ticket Number</th>
                  <th className="px-4 py-3">Payment ID</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Total Fare</th>
                  <th className="px-4 py-3">Giftcard Status</th>
                  <th className="px-4 py-3">Applied Amount</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>

              <tbody className="border-t">
                {displayEligibleRows.map((r) => (
                  <tr key={r.trace_id ? `trace-${r.trace_id}` : r.booking_id} className="border-t hover:bg-gray-50">
                    {/* <td className="px-4 py-3 font-semibold">{r.booking_id}</td> */}
                    <td className="px-4 py-3 font-semibold">{r.booking_ticket_number}</td>
                    <td className="px-4 py-3">{r.payment_id || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.user_name || "-"}</div>
                      <div className="text-xs text-gray-600">{r.user_email || r.user_id}</div>
                    </td>
                    <td className="px-4 py-3">₹{money2(toNum(r.totalfare))}</td>
                    <td className="px-4 py-3">
                      {Number(r.is_giftcard_fully_claimed) === 1 ? (
                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                          FULLY CLAIMED
                        </span>
                      ) : (
                        <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                          PARTIAL
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">₹{money2(toNum(r.applied_giftcard_amount))}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openRefundModal(r)}
                      >
                        Refund
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        ) : historyRows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No refunds yet.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Refund ID</th>
                <th className="px-4 py-3">Booking ID</th>
                <th className="px-4 py-3">Payment ID</th>
                <th className="px-4 py-3">Refundable</th>
                <th className="px-4 py-3">To Wallet</th>
                <th className="px-4 py-3">To Payment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {historyRows.map((x) => (
                <tr key={x.refund_giftcard_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{x.refund_id || "Refunded to GiftCard"  || x.refund_giftcard_id}</td>
                  <td className="px-4 py-3">{x.booking_id}</td>
                  <td className="px-4 py-3">{x.payment_id || "-"}</td>
                  <td className="px-4 py-3">₹{money2(toNum(x.overall_refundable_amount))}</td>
                  <td className="px-4 py-3">₹{money2(toNum(x.amount_to_giftcard_wallet))}</td>
                  <td className="px-4 py-3">₹{money2(toNum(x.to_user_refund_amount_paise) / 100)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        x.status === "COMPLETED"
                          ? "bg-green-100 text-green-800"
                          : x.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {x.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {x.created_at ? new Date(x.created_at).toLocaleString() : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Refund Modal */}
      {refundOpen && selected ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-lg my-8">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b px-6 py-4">
              <div>
                <div className="text-2xl font-bold">Gift Card Refund</div>
                <div className="text-sm text-gray-600 mt-1">
                  Booking: <b>{selected.booking_ticket_number}</b>
                  {selected.payment_id ? (
                    <>
                      {" "}
                      • Payment: <b>{selected.payment_id}</b>
                    </>
                  ) : null}
                </div>
              </div>

              <button
                type="button"
                onClick={closeRefundModal}
                className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <form onSubmit={submitGiftcardRefund} className="px-6 py-5">
              {/* Cancellation Type */}
              <div className="mb-5">
                <label className="text-sm font-semibold">
                  Cancellation Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  value={form.cancel_type}
                  onChange={(e) => setField("cancel_type", e.target.value)}
                >
                  <option value="-Select-">-Select-</option>
                  <option value="Refund with Airline Penalty/ Void">Refund with Airline Penalty/ Void</option>
                  <option value="Partial Cancellation">Partial Cancellation</option>
                  <option value="Flight Not Operational">Flight Not Operational</option>
                  <option value="Change Itinerary / Reissue">Change Itinerary / Reissue</option>
                </select>
              </div>

              {/* Refund Type */}
              <div className="mb-5">
                <label className="text-sm font-semibold">
                  Refund Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  value={form.refund_type}
                  onChange={(e) => {
                    const v = e.target.value;
                    setField("refund_type", v);
                    if (v !== "INSTANT_REFUND") setField("instant_refund_reason", "");
                  }}
                >
                  <option value="NORMAL_REFUND">Normal Refund</option>
                  <option value="INSTANT_REFUND">Instant Refund</option>
                </select>
              </div>

              {form.refund_type === "INSTANT_REFUND" ? (
                <div className="mb-5">
                  <label className="text-sm font-semibold">
                    Why Instant Refund? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black min-h-[100px]"
                    placeholder="Enter reason for instant refund..."
                    value={form.instant_refund_reason}
                    onChange={(e) => setField("instant_refund_reason", e.target.value)}
                  />
                </div>
              ) : null}

              <div className="border rounded-2xl p-4 bg-gray-50">
                <div className="text-lg font-bold mb-4">Booking Amount Details</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold">Total Fare</label>
                    <input
                      className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-white"
                      value={`₹${money2(totalFareNum)}`}
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Convenience Fee</label>
                    <input
                      className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-white"
                      value={`₹${money2(convenienceFeeNum)}`}
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Giftcard Status</label>
                    <input
                      className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-white font-semibold"
                      value={isGiftcardFullyClaimed ? "FULLY CLAIMED" : "PARTIAL"}
                      readOnly
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      {isGiftcardFullyClaimed
                        ? "100% of payment via giftcard (is_giftcard_fully_claimed = 1)"
                        : "Partially paid via giftcard"}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Applied Giftcard Amount</label>
                    <input
                      className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-white"
                      value={`₹${money2(appliedGiftcardAmountNum)}`}
                      readOnly
                    />
                  </div>
                </div>

                {/* show max cap info when partial giftcard */}
                {!isGiftcardFullyClaimed ? (
                  <div className="mt-3 text-xs text-gray-700">
                    Max refundable cap (partial giftcard rule):{" "}
                    <b>₹{money2(Math.max(0, totalFareNum - convenienceFeeNum - appliedGiftcardAmountNum))}</b>{" "}
                    (Total - Convenience Fee - Giftcard Amount)
                  </div>
                ) : null}
              </div>

              {/* Cancellation Amount */}
              <div className="mt-5">
                <label className="text-sm font-semibold">Cancellation / Airline Charges</label>
                <input
                  className="mt-1 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  value={form.cancellation_amount}
                  onChange={(e) => setField("cancellation_amount", e.target.value)}
                  placeholder="Enter cancellation amount (0 if none)"
                  disabled={form.full_payment}
                />
                <div className="mt-1 text-xs text-gray-500">
                  {form.full_payment
                    ? "Disabled when Full Payment is checked"
                    : `Must be ≤ Total Fare (max ₹${money2(maxCancellationAllowed)})`}
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.full_payment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setField("full_payment", checked);
                      if (checked) setField("cancellation_amount", "0");
                    }}
                  />
                  <span className="font-semibold">Full Payment Refund (no cancellation charges)</span>
                </label>
              </div>

              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.cancel_insurance}
                    onChange={(e) => setField("cancel_insurance", e.target.checked)}
                  />
                  <span className="font-semibold">Cancel Insurance Booking</span>
                </label>
              </div>

              <div className="mt-6 rounded-xl bg-blue-50 border border-blue-200 p-4">
                <div className="text-base font-bold mb-3 text-blue-900">Refund Calculation Preview</div>

                {/* <div className="text-sm text-blue-900 mb-2 font-semibold">
                  {refundCalculation.case_description}
                </div> */}

                {refundCalculation?.errors?.length ? (
                  <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {refundCalculation.errors.map((x, i) => (
                      <div key={i}>• {x}</div>
                    ))}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-gray-600 text-xs mb-1">Overall Refundable Amount</div>
                    <div className="text-xl font-bold text-gray-900">
                      ₹{money2(refundCalculation.overall_refundable_amount)}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3">
                    <div className="text-gray-600 text-xs mb-1">Giftcard Refund Amount</div>
                    <div className="text-xl font-bold text-green-700">
                      ₹{money2(refundCalculation.giftcard_refund_amount)}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3">
                    <div className="text-gray-600 text-xs mb-1">To Giftcard Wallet</div>
                    <div className="text-xl font-bold text-green-700">
                      ₹{money2(refundCalculation.amount_to_giftcard_wallet)}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3">
                    <div className="text-gray-600 text-xs mb-1">To Payment Method (User)</div>
                    <div className="text-xl font-bold text-purple-700">
                      ₹{money2(refundCalculation.to_user_refund_amount)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <label className="text-sm font-semibold">
                  Remarks <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black min-h-[120px]"
                  placeholder="Enter remarks about this refund..."
                  value={form.remarks}
                  onChange={(e) => setField("remarks", e.target.value)}
                />
              </div>

              <div className="mt-5">
                <label className="text-sm font-semibold">Attach Document (Optional)</label>
                <input
                  className="mt-2 block w-full text-sm"
                  type="file"
                  onChange={(e) => setField("attachment", e.target.files?.[0] || null)}
                />
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  onClick={closeRefundModal}
                  className="rounded-xl border px-5 py-2 text-sm font-semibold hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || (refundCalculation?.errors?.length ?? 0) > 0}
                  className="rounded-xl bg-black px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  title={
                    refundCalculation?.errors?.length
                      ? refundCalculation.errors[0]
                      : undefined
                  }
                >
                  {submitting ? "Processing..." : "Process Refund"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
